import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { MdClose } from 'react-icons/md';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import theme from '../../theme';
import { GRADE_LABELS } from '../../utils/formatter';
import { useUserStore } from '../../stores/userStore';
import { REFERRER_TYPE } from '../../constants/gaEnums';
import { usersApi } from '../../api/users';

const getNotificationMessage = (notification) => {
  const { type, targetTitleSnapshot, newGrade } = notification;
  const target = targetTitleSnapshot || '';

  switch (type) {
    case 'FOLLOW':
      return '회원님을 팔로우했습니다.';
    case 'FOLLOWING_REVIEW_CREATED':
      return `${target}에 새로운 리뷰를 남겼습니다.`;
    case 'FOLLOWING_PLAYLIST_CREATED':
      return `${target} 플리를 등록했습니다.`;
    case 'REVIEW_LIKE':
      return `회원님의 ${target} 리뷰를 좋아합니다.`;
    case 'COMMENT_LIKE':
      return `회원님의 댓글을 좋아합니다.`;
    case 'COMMENT_ON_REVIEW':
      return `회원님의 ${target} 리뷰에 댓글을 남겼습니다.`;
    case 'COMMENT_ON_LIST':
      return `회원님의 ${target} 믹스에 댓글을 남겼습니다.`;
    case 'COMMENT_ON_NEWS':
      return `회원님의 ${target} 뉴스에 댓글을 남겼습니다.`;
    case 'COMMENT_ON_NOTICE':
      return `회원님의 ${target} 공지에 댓글을 남겼습니다.`;
    case 'REPLY_ON_COMMENT':
      return `회원님의 댓글에 답글을 남겼습니다.`;
    case 'GRADE_CHANGE':
      return `${GRADE_LABELS[newGrade] || newGrade} 회원으로 등급 변경되었습니다.`;
    case 'COMMENT_DELETED':
      return `관리자에 의해 회원님의 댓글이 삭제되었습니다.`;
    case 'REVIEW_DELETED':
      return `관리자에 의해 회원님의 ${target} 리뷰가 삭제되었습니다.`;
    case 'PLAYLIST_DELETED':
      return `관리자에 의해 회원님의 ${target} 믹스가 삭제되었습니다.`;
    case 'REVIEW_FEATURED':
      return `회원님의 ${target} 리뷰가 홈에 피처드 되었습니다.`;
    case 'PROFILE_IMAGE_DELETED':
      return `관리자에 의해 회원님의 프로필 사진이 삭제되었습니다.`;
    case 'BIO_DELETED':
      return `관리자에 의해 회원님의 소개글이 삭제되었습니다.`;
    default:
      return '새로운 알림이 있습니다.';
  }
};

const REFRESH_USER_TYPES = ['PROFILE_IMAGE_DELETED', 'BIO_DELETED', 'GRADE_CHANGE'];

const NotificationItem = ({ notification, onMarkAsRead, onDelete, isFirst = false }) => {
  const navigate = useNavigate();
  const fetchUser = useUserStore((state) => state.fetchUser);

  const handleClick = () => {
    if (!notification.isRead) {
      onMarkAsRead(notification.id);
    }
    if (REFRESH_USER_TYPES.includes(notification.type)) {
      fetchUser();
    }
    if (notification.link) {
      navigate(notification.link, { state: { referrerType: REFERRER_TYPE.NOTIFICATION } });
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(notification.id);
  };

  const handleActorClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!notification.isRead) {
      onMarkAsRead(notification.id);
    }

    try {
      if (notification.actorId) {
        const user = (await usersApi.getUserById(notification.actorId)).data;
        navigate(`/users/${encodeURIComponent(user.nickname)}`, {
          state: { referrerType: REFERRER_TYPE.NOTIFICATION },
        });
        return;
      }
    } catch (_) {}

    const fallbackLink =
      notification.actorProfileLink ||
      `/users/${encodeURIComponent(notification.actorNicknameSnapshot)}`;
    navigate(fallbackLink, { state: { referrerType: REFERRER_TYPE.NOTIFICATION } });
  };

  const message = getNotificationMessage(notification);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return '방금 전';
    if (diffMins < 60) return `${diffMins}분 전`;
    if (diffHours < 24) return `${diffHours}시간 전`;
    if (diffDays < 7) return `${diffDays}일 전`;

    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${year}.${month}.${day}`;
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: { xs: 1, md: 1.5 },
        py: 1.25,
        pr: 1,
        backgroundColor: notification.isRead ? theme.palette.grey.bg : 'white',
        borderTop: isFirst ? `1px solid ${theme.palette.grey.line}` : 'none',
        borderBottom: `1px solid ${theme.palette.grey.line}`,
        cursor: notification.link ? 'pointer' : 'default',
        transition: 'background-color 0.2s',
        '&:hover': {
          backgroundColor: notification.isRead ? theme.palette.grey[200] : theme.palette.grey[50],
        },
      }}
    >
      {/* 읽지 않음 표시 */}
      <Box
        sx={{
          width: 16,
          height: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {!notification.isRead && (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: 1,
              backgroundColor: theme.palette.secondary.main,
            }}
          />
        )}
      </Box>

      {/* 알림 내용 */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* 내용 박스 */}
        <Box sx={{ mb: { xs: 1.5, lg: 2 } }}>
          <Typography
            fontSize={{ xs: 14, lg: 16 }}
            lineHeight={1.5}
            color={theme.palette.black[6]}
            sx={{
              wordBreak: 'break-word',
              mb: { xs: 1, lg: 1.5 },
              ...(notification.contentPreview && {
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }),
            }}
          >
            {notification.actorNicknameSnapshot && (
              <>
                <Typography
                  component={RouterLink}
                  to={
                    notification.actorProfileLink ||
                    `/users/${encodeURIComponent(notification.actorNicknameSnapshot)}`
                  }
                  onClick={handleActorClick}
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: 14, lg: 16 },
                    lineHeight: 1.5,
                    color: theme.palette.black[6],
                    textDecoration: 'none',
                  }}
                >
                  {notification.actorNicknameSnapshot}
                </Typography>
                님이{' '}
              </>
            )}
            {notification.contentPreview ? message.replace(/\.$/, '') : message}
            {notification.contentPreview && `: ${notification.contentPreview}`}
          </Typography>

          {notification.reason && (
            <Typography
              fontSize={{ xs: 13, md: 14, lg: 16 }}
              color={theme.palette.black[6]}
              lineHeight={1.5}
            >
              사유: {notification.reason}
            </Typography>
          )}
        </Box>

        {/* 날짜 박스 */}
        <Box>
          <Typography
            fontSize={{ xs: 12, md: 13, lg: 14 }}
            color={{ xs: theme.palette.black[2], md: theme.palette.black[4] }}
          >
            {formatDate(notification.createdAt)}
          </Typography>
        </Box>
      </Box>

      {/* 삭제 버튼 */}
      <IconButton
        disableRipple
        onClick={handleDelete}
        sx={{
          width: 16,
          height: 16,
          padding: 0,
          flexShrink: 0,
          color: theme.palette.black[3],
          '&:hover': {
            color: theme.palette.black[6],
          },
        }}
      >
        <MdClose size={16} />
      </IconButton>
    </Box>
  );
};

export default NotificationItem;
